import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Package, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  shipped: "bg-violet-100 text-violet-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState(null);

  const loadOrders = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => toast.error("Could not load orders"));
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (user) loadOrders();
  }, [user, loadOrders]);

  const updateStatus = async (orderId, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? data : o)));
      toast.success(`Order marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) {
    return <div data-testid="admin-loading" className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading...</div>;
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex bg-muted/30">
      <aside className="w-60 shrink-0 bg-slate-950 text-slate-200 flex flex-col min-h-screen sticky top-0">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
          <span className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
            <Sun className="w-4 h-4 text-white" />
          </span>
          <span className="font-heading font-semibold text-sm leading-tight">Jehovah Nissi<br />Solar Agency</span>
        </div>
        <nav className="flex-1 p-4">
          <div data-testid="admin-nav-orders" className="flex items-center gap-2 rounded-md bg-slate-800 text-white px-3 py-2 text-sm font-medium">
            <Package className="w-4 h-4" /> Orders
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <p className="text-xs text-slate-400 truncate mb-3">{user.email}</p>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 sm:p-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-1">Dashboard</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Orders</h1>
          </div>
          <p data-testid="orders-count" className="text-sm text-muted-foreground">{orders ? `${orders.length} order${orders.length === 1 ? "" : "s"}` : ""}</p>
        </div>

        <div className="bg-white border border-border rounded-md overflow-hidden">
          <Table data-testid="orders-table">
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders && orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} data-testid="orders-empty" className="text-center text-sm text-muted-foreground py-12">
                    No orders yet. Orders placed on the store will appear here.
                  </TableCell>
                </TableRow>
              )}
              {orders && orders.map((order) => (
                <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                  <TableCell className="font-mono text-xs max-w-[120px] truncate">{order.id.slice(0, 8)}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{order.customer.name}</p>
                    <p className="text-xs text-muted-foreground">{order.customer.phone}</p>
                    <p className="text-xs text-muted-foreground max-w-[220px] truncate" title={order.customer.address}>{order.customer.address}</p>
                  </TableCell>
                  <TableCell className="text-xs max-w-[220px]">
                    {order.items.map((i) => (
                      <p key={i.product_id} className="truncate">{i.name} &times; {i.qty}</p>
                    ))}
                  </TableCell>
                  <TableCell className="font-semibold text-sm">{inr(order.total)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                  <TableCell>
                    <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                      <SelectTrigger data-testid={`status-select-${order.id}`} className={`w-32 h-8 text-xs font-medium border-0 ${statusColors[order.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} data-testid={`status-option-${order.id}-${s}`} value={s} className="text-xs capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!orders && <p data-testid="orders-loading" className="text-sm text-muted-foreground p-8">Loading orders...</p>}
        </div>
      </main>
    </div>
  );
}
